class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams !== FacetFiltersForm.searchParamsPrev) FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => element.classList.toggle('disabled', disable));
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collection = productGridContainer?.querySelector('.collection');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.remove('hidden'));
    collection?.classList.add('loading');
    countContainer?.classList.add('loading');
    countContainerDesktop?.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount, 10) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;
    let deferred;

    if (isSearchPage && SearchUpdateEvent) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(new SearchUpdateEvent({
        search: {
          query: urlSearchParams.get('q') || '',
          productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
        },
        promise: deferred.promise,
      }));
    } else if (isCollectionPage && CollectionUpdateEvent) {
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(new CollectionUpdateEvent({
        collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
        productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
        sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
        promise: deferred.promise,
      }));
    }

    if (!deferred) return;
    return {
      resolve: (filteredCount) => deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount }),
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl)?.html;
    if (html) FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    const productGrid = document.getElementById('ProductGridContainer');
    if (typeof initializeScrollAnimationTrigger === 'function' && productGrid) {
      initializeScrollAnimationTrigger(productGrid);
    }
  }

  static renderProductGridContainer(html) {
    const parsedGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    const productGrid = document.getElementById('ProductGridContainer');
    if (!parsedGrid || !productGrid) return;
    productGrid.innerHTML = parsedGrid.innerHTML;
    productGrid.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!sourceCount || !container) return;

    container.innerHTML = sourceCount.innerHTML;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount, 10) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const fetched = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const current = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');

    Array.from(current).forEach((element) => {
      if (!Array.from(fetched).some(({ id }) => element.id === id)) element.remove();
    });

    const eventTarget = event?.target instanceof Element ? event.target : null;
    const sourceFilter = eventTarget?.closest('.js-filter');
    const matchesId = (element) => sourceFilter ? element.id === sourceFilter.id : false;
    const facetsToRender = Array.from(fetched).filter((element) => !matchesId(element));
    const countsToRender = Array.from(fetched).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const existing = document.getElementById(elementToRender.id);
      if (existing) {
        existing.innerHTML = elementToRender.innerHTML;
      } else if (index > 0) {
        const previous = facetsToRender[index - 1];
        if (elementToRender.className === previous.className) {
          document.getElementById(previous.id)?.after(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && sourceFilter) {
      FacetFiltersForm.renderCounts(countsToRender, sourceFilter);
      FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(sourceFilter.id));
      const newFilter = document.getElementById(sourceFilter.id);
      if (newFilter && eventTarget?.getAttribute('type') !== 'text') {
        const matchingInput = eventTarget.id ? newFilter.querySelector(`#${CSS.escape(eventTarget.id)}`) : null;
        const fallback = newFilter.querySelector(newFilter.classList.contains('mobile-facets__details') ? '.mobile-facets__close-button' : '.facets__summary');
        (matchingInput || fallback)?.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const pairs = [['.facets__summary', '.facets__summary'], ['.facets__header', '.facets__header']];
    pairs.forEach(([sourceSelector, targetSelector]) => {
      const sourceElement = source.querySelector(sourceSelector);
      const targetElement = target.querySelector(targetSelector);
      if (sourceElement && targetElement) targetElement.outerHTML = sourceElement.outerHTML;
    });

    const targetWrap = target.querySelector('.facets-wrap');
    const sourceWrap = source.querySelector('.facets-wrap');
    if (sourceWrap && targetWrap) {
      if (target.querySelector('show-more-button .label-show-more.hidden')) {
        sourceWrap.querySelectorAll('.facets__item.hidden').forEach((item) => item.classList.replace('hidden', 'show-more-item'));
      }
      targetWrap.outerHTML = sourceWrap.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!target) return;
    const targetList = target.querySelector('.mobile-facets__list');
    const sourceList = source.querySelector('.mobile-facets__list');
    if (sourceList && targetList) targetList.outerHTML = sourceList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    return productGrid?.dataset.id ? [{ section: productGrid.dataset.id }] : [];
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const target = event.target instanceof Element ? event.target : null;
    const form = target?.closest('form');
    if (!form) return;

    if (event.srcElement?.classList?.contains('mobile-facets__checkbox')) {
      this.onSubmitForm(this.createSearchParams(form), event);
      return;
    }

    const isMobile = form.id === 'FacetFiltersFormMobile';
    const forms = [];
    document.querySelectorAll('facet-filters-form form').forEach((facetForm) => {
      if (isMobile) {
        if (facetForm.id === 'FacetFiltersFormMobile') forms.push(this.createSearchParams(facetForm));
      } else if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(facetForm.id)) {
        forms.push(this.createSearchParams(facetForm));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget?.href || '';
    const searchParams = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    if (!event.key.match(/[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.addEventListener('click', this.closeFilter.bind(this));
    link.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.closeFilter(event);
      }
    });
  }

  closeFilter(event) {
    event.preventDefault();
    (this.closest('facet-filters-form') || document.querySelector('facet-filters-form'))?.onActiveFilterClick(event);
  }
}
if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);
